import { Controller } from "@hotwired/stimulus"
import { EFFECTS, mountHeroEffect } from "../hero_effects"

// The engine's own clearance between its anchor and the nearest drawn pixel.
const GAP = 12

// Mounts one of the pixel-art effects beside a hero's title. The element is a
// layer laid over the hero by CSS; the title and the line to stay above are
// found in the hero around it, by the selectors the component was given.
//
// The art scales with the title. `pixelSize` is the size of one art pixel when
// the title is at its largest, and on a narrower screen it shrinks in the same
// proportion as the title's type, rounded to a whole pixel because a
// fractional one draws uneven, blurred pixels. The engine reads it again on
// every layout, and it lays out again whenever the title changes size.
//
// **Always from the title's edges, and not at all on a phone.** The effects
// never move somewhere else when the screen narrows (the owner's choice of
// 2026-10-07); below 768px, where the title leaves almost nothing beside it,
// the stylesheet hides the layer instead (the owner's choice of 2026-10-08).
// A hidden layer has no size and the engine cannot draw into one, so the
// effect runs only while the layer has a size, and starts or stops as the
// window crosses that width.
//
// Decoration only: the page is complete without it, and nothing here is read
// out or takes a click.
export default class extends Controller {
  static values = { effect: String, pixelSize: Number, title: String, avoid: String }

  connect() {
    if (!EFFECTS[this.effectValue]) return

    this.hero = this.element.parentElement
    this.title = this.hero.querySelector(this.titleValue)
    if (!this.title) return

    this.sizer = new ResizeObserver(() => this.#fit())
    this.sizer.observe(this.element)
    this.#fit()
  }

  // Running while the layer has a size, and not while it has none.
  #fit() {
    const sized = this.element.clientWidth > 0 && this.element.clientHeight > 0
    if (sized && !this.effect) this.#mount()
    if (!sized && this.effect) this.#unmount()
  }

  #mount() {
    this.effect = mountHeroEffect(this.element, {
      effect: this.effectValue,
      title: this.title,
      // A hero with nothing under its title names no line to stay above, and
      // an empty selector is a SyntaxError from querySelector, not a null.
      avoid: this.avoidValue ? this.hero.querySelector(this.avoidValue) : null,
      gap: GAP,
      minSpace: 0,
      pixelSize: () => this.#pixelSize()
    })
  }

  // A new effect named on the element takes over the same canvas, which is
  // how the effects are compared on a running page.
  effectValueChanged() {
    if (this.effect && EFFECTS[this.effectValue]) this.effect.setEffect(this.effectValue)
  }

  disconnect() {
    this.sizer?.disconnect()
    this.sizer = null
    this.#unmount()
  }

  #unmount() {
    this.effect?.destroy()
    this.effect = null
  }

  #pixelSize() {
    const scale = parseFloat(getComputedStyle(this.title).fontSize) / this.#largestTitle()
    return Math.max(1, Math.round(this.pixelSizeValue * Math.min(1, scale)))
  }

  // The ceiling of the title's size, read from the stylesheet that sets it, so
  // the two cannot disagree. It is written in rem.
  #largestTitle() {
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize)
    return parseFloat(getComputedStyle(this.hero).getPropertyValue("--hero-title-largest")) * rem
  }
}
